/**
 * Der Bereichsfilter als querschnittliche Funktion.
 *
 * Jede Modulliste - Kalender, Aufgaben, spaeter Ziele und Gewohnheiten - filtert
 * ueber genau diese Helfer. Dadurch wirkt ein Klick auf "nur Gesundheit"
 * ueberall gleichzeitig, und es gibt keine Modulliste, die den Filter vergisst.
 */
import { useUiStore } from '@/store/ui';
import type { ID } from '@/domain/types';

/** Ein leerer Filter bedeutet "alles anzeigen". */
export function matchesAreaFilter(areaId: ID | null | undefined, activeAreaIds: ID[]): boolean {
  if (activeAreaIds.length === 0) return true;
  return areaId != null && activeAreaIds.includes(areaId);
}

/**
 * Bereich fuer einen NEU angelegten Eintrag.
 *
 * Ein aktiver Filter blendet alles aus, was keinem der gefilterten Bereiche
 * angehoert. Ein neuer Eintrag ohne Bereich verschwaende deshalb im selben
 * Moment, in dem man ihn anlegt - gespeichert, aber unsichtbar (Roadmap T-01).
 * Darum erbt er den ersten gefilterten Bereich. Ohne Filter bleibt er
 * bereichslos und ist trotzdem sichtbar.
 *
 * Jede Stelle, die etwas Neues anlegt, nimmt ihren Vorgabebereich von hier -
 * sonst taucht dieselbe Falle beim naechsten Modul wieder auf.
 */
export function defaultAreaForNew(activeAreaIds: ID[]): ID | null {
  return activeAreaIds[0] ?? null;
}

export function applyAreaFilter<T extends { areaId: ID | null }>(
  rows: T[],
  activeAreaIds: ID[],
): T[] {
  if (activeAreaIds.length === 0) return rows;
  return rows.filter((r) => matchesAreaFilter(r.areaId, activeAreaIds));
}

/** Hook-Variante fuer Komponenten. */
export function useAreaFilter() {
  const activeAreaIds = useUiStore((s) => s.activeAreaIds);
  const toggleArea = useUiStore((s) => s.toggleArea);
  const clearAreaFilter = useUiStore((s) => s.clearAreaFilter);

  return {
    activeAreaIds,
    isActive: activeAreaIds.length > 0,
    toggleArea,
    clearAreaFilter,
    matches: (areaId: ID | null | undefined) => matchesAreaFilter(areaId, activeAreaIds),
    filter: <T extends { areaId: ID | null }>(rows: T[]) => applyAreaFilter(rows, activeAreaIds),
  };
}
